import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2, ShieldAlert, Undo2 } from 'lucide-react';
import { pentestsAPI } from '../../../../services/api';

/**
 * Raising authority on a production host, inline on the plan screen.
 *
 * The framing here matters and it is easy to get wrong. Production does NOT
 * lock a target and this box must never imply that it does. The engagement
 * floor applies to a production host exactly as it does to a beta one; what
 * production costs is a written justification whenever you raise ABOVE confirm,
 * and a paragraph in the report saying you did.
 *
 * That is deliberate. Incident validation on production is authorised,
 * recurring work, and a system that refuses it is one that gets bypassed with a
 * shell script, after which nothing is recorded at all. So the backend answers
 * a missing justification with a 400 asking a question rather than a 403, and
 * this box asks the same question before the request is sent.
 *
 * It sits on the plan screen rather than only on Pre-Engagement because this is
 * where the operator can see what the raise is FOR: the steps that need it are
 * named a few pixels away.
 */

const LEVELS = ['confirm', 'prove', 'exploit'];
const RANK = { confirm: 1, prove: 2, exploit: 3 };

const WHAT_IT_PERMITS = {
  confirm: 'establish that a flaw exists, reading nothing back',
  prove: 'read back one bounded value as demonstrated impact',
  exploit: 'attempt destructive-capability probes to establish a ceiling',
};

// The same three the API offers in its 400. Kept in step deliberately: an
// operator who hits the error and then finds different wording here has to
// work out whether they are the same thing.
const REASONS = [
  'Incident replay: reproducing observed attacker steps',
  'Authorised production test, approved by',
  'Validation of a reported fix',
];

function targetIdsIn(nodes) {
  const ids = new Set();
  (nodes || []).forEach((node) => {
    [node.ref, ...(node.based_on || [])].forEach((citation) => {
      const match = /^target:(\d+)$/.exec((citation || '').trim());
      if (match) ids.add(Number(match[1]));
    });
  });
  return ids;
}

export default function AuthorityBox({ pentestId, nodes, onChanged }) {
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [level, setLevel] = useState('prove');
  const [reason, setReason] = useState(REASONS[0]);
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setScope(await pentestsAPI.scope.get(pentestId));
    } catch {
      setScope(null);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => { load(); }, [load]);

  const touched = useMemo(() => targetIdsIn(nodes), [nodes]);

  // Every production target the plan actually touches. A production host the
  // plan never goes near is not a decision the operator has to make here.
  const production = useMemo(
    () => (scope?.targets || []).filter(
      (t) => t.sensitivity === 'production' && touched.has(t.id),
    ),
    [scope, touched],
  );

  // What the plan says it needs, per target, so the box can say what the raise
  // is FOR rather than asking for authority in the abstract.
  const needed = useMemo(() => {
    const out = {};
    (nodes || []).forEach((node) => {
      if (!node.level_required || node.level_required === 'confirm') return;
      targetIdsIn([node]).forEach((id) => {
        if (!out[id]) out[id] = [];
        out[id].push(node);
      });
    });
    return out;
  }, [nodes]);

  if (loading || !production.length) return null;

  const open = (target) => {
    setOpenId(target.id);
    setError(null);
    const wanted = (needed[target.id] || [])
      .map((n) => n.level_required)
      .sort((a, b) => RANK[b] - RANK[a])[0];
    setLevel(wanted || 'prove');
    setDetail('');
  };

  const grant = async (target) => {
    setBusy(true);
    setError(null);
    try {
      await pentestsAPI.scope.setLevel(pentestId, target.id, {
        exploit_level: level,
        authority_ref: `${reason} ${detail}`.trim(),
      });
      setOpenId(null);
      await load();
      await onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.reason || 'Could not record the raise.'));
    } finally {
      setBusy(false);
    }
  };

  const clear = async (target) => {
    setBusy(true);
    setError(null);
    try {
      await pentestsAPI.scope.setLevel(pentestId, target.id, { exploit_level: null });
      await load();
      await onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not return it to the floor.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/50">
      <div className="flex items-start gap-2 border-b border-amber-200 px-4 py-2.5">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div>
          <h4 className="text-sm font-semibold text-amber-900">
            Production {production.length === 1 ? 'host' : 'hosts'} in this plan
          </h4>
          <p className="mt-0.5 text-xs leading-relaxed text-amber-800">
            Production is not off-limits and nothing here is blocked. The engagement
            floor applies exactly as it does anywhere else. What production costs is
            a written reason whenever you raise a target <em>above</em> confirm, and
            a paragraph in the report saying you did.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 border-b border-amber-200 bg-red-50 px-4 py-2 text-xs text-red-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="divide-y divide-amber-200">
        {production.map((target) => {
          const raised = RANK[target.effective_level] > RANK.confirm;
          const wants = needed[target.id] || [];
          return (
            <div key={target.id} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs font-semibold text-slate-900">
                  {target.value}
                </span>
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                  raised ? 'bg-amber-200 text-amber-900' : 'bg-slate-200 text-slate-600'
                }`}>
                  {target.effective_level}
                </span>
                {target.inherited && (
                  <span className="text-[11px] text-slate-500">inheriting the floor</span>
                )}
                {raised && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => clear(target)}
                    className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
                  >
                    <Undo2 className="h-3 w-3" /> Return it to the floor
                  </button>
                )}
              </div>

              {wants.length > 0 ? (
                <p className="mt-1 text-xs text-amber-900">
                  {wants.length} step{wants.length === 1 ? '' : 's'} in this plan
                  {wants.length === 1 ? ' needs' : ' need'}{' '}
                  <b>{wants.map((n) => n.level_required).sort((a, b) => RANK[b] - RANK[a])[0]}</b>{' '}
                  here: {wants.map((n) => n.label).join(', ')}.
                </p>
              ) : (
                <p className="mt-1 text-xs text-slate-600">
                  No step in this plan asks for more than the floor on this host. You can
                  still raise it in advance, so an agent that reaches the ceiling does not
                  have to stop and ask.
                </p>
              )}

              {raised && target.level_authority_ref && (
                <div className="mt-2 rounded-md border border-amber-200 bg-white p-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    What the report will say
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-slate-700">
                    &ldquo;{target.value} is a production host. It was tested above the
                    engagement floor at {target.effective_level.charAt(0).toUpperCase()
                    + target.effective_level.slice(1)} level, which permits reading data
                    back out of the host. Authority recorded:{' '}
                    {target.level_authority_ref}.&rdquo;
                  </p>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Printed whether or not anything is found.
                  </p>
                </div>
              )}

              {openId === target.id ? (
                <div className="mt-2 rounded-md border border-amber-300 bg-white p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <label htmlFor={`lvl-${target.id}`} className="text-xs font-semibold text-slate-700">
                      Raise to
                    </label>
                    <select
                      id={`lvl-${target.id}`}
                      value={level}
                      onChange={(e) => setLevel(e.target.value)}
                      className="rounded border border-slate-300 px-2 py-1 text-xs"
                    >
                      {LEVELS.filter((l) => l !== 'confirm').map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                    <span className="text-[11px] text-slate-500">
                      lets an agent {WHAT_IT_PERMITS[level]}
                    </span>
                  </div>

                  <label htmlFor={`why-${target.id}`}
                         className="mt-3 block text-xs font-semibold text-slate-700">
                    Why are you testing production?{' '}
                    <span className="font-normal text-red-600">required</span>
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                  >
                    {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <textarea
                    id={`why-${target.id}`}
                    rows={3}
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    placeholder="SI-34392 follow-up. An external party already reached this host; we are reproducing their exact steps to establish what was and was not possible. Approved by me as red team manager."
                    className="mt-1 w-full rounded border border-slate-300 p-2 text-xs focus:border-wasabi-500 focus:outline-none"
                  />
                  <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                    Recorded against your user, timestamped, written to the audit trail,
                    and printed in the engagement report whether or not anything is found.
                  </p>

                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      disabled={busy || !detail.trim()}
                      onClick={() => grant(target)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      Grant {level} on {target.value}
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenId(null)}
                      className="rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => open(target)}
                  className="mt-2 rounded-md border border-amber-400 bg-white px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50"
                >
                  {raised ? 'Change the authority on this host' : 'Raise authority on this host'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="border-t border-amber-200 px-4 py-2 text-[11px] text-amber-800">
        Granting here does not start anything. An agent parked on a raise picks it up
        the next time the engagement advances, and everything else still waits for you
        to approve the plan.
      </p>
    </div>
  );
}
